import { chakra, defineRecipe } from '@plurall/elo-v3';

import { GlobalNavigation } from '@/components/AppLayout/components/GlobalNavigation/GlobalNavigation';
import { LocalNavigation } from '@/components/AppLayout/components/LocalNavigation';
import { WelcomeSection } from '@/components/AppLayout/components/WelcomeSection/WelcomeSection';

const gridRecipe = defineRecipe({
  className: 'grid',
  base: {
    '--max-content-width': '75rem',
    '--topbar-height': '3.5rem',
    '--sidebar-width-collapsed': '3.5rem',
    '--sidebar-width-expanded': '15.625rem',
    '--sidebar-transition-duration': '.55s',
    '--sidebar-width-diff': '0',
    '--sidebar-width': 'var(--sidebar-width-collapsed)',
    '--welcome-height': '0rem',
    '--labels-display': 'block',
    '--divider-display': 'block',
    '--label-display': 'inline',
    '--label-opacity': '1',
    '--menu-title-display': 'block',
    '--menu-separator-display': 'none',
    '--sidebar-translate-x': '-100%',
    '--local-nav-title-gap': '0.7rem',
    '--local-nav-indicator-display': 'block',

    display: 'grid',
    minBlockSize: '100svh',
    gridTemplateRows: 'var(--topbar-height) auto 1fr',
    gridTemplateColumns: '[full-start] auto [full-end]',

    '&[data-open="true"]': {
      '--sidebar-translate-x': '0',
      '--welcome-height': '5rem',
      '& .welcome': {
        '--swap-slot-opacity': '1',
      },
    },

    sm: {
      '--labels-display': 'none',
      '--divider-display': 'block',
      '--sidebar-translate-x': '0',
      '--label-opacity': '0',
      '--menu-title-display': 'none',

      gridTemplateRows: 'var(--topbar-height) 1fr',
      gridTemplateColumns:
        '[full-start sidebar-start sidebar-stack-start] var(--sidebar-width) [sidebar-end content-start] var(--sidebar-width-diff) [sidebar-stack-end] 1fr [content-end] auto [full-end]',

      willChange: 'grid-template-columns, grid-template-rows',
      transitionProperty: 'grid-template-columns, grid-template-rows',
      transitionDuration: 'var(--sidebar-transition-duration)',
      transitionTimingFunction: 'cubic-bezier(0.77, 0, 0.18, 1)',
      transitionBehavior: 'allow-discrete',

      '--sidebar-width': 'var(--sidebar-width-collapsed)',

      '&[data-open="true"]': {
        '--sidebar-width': 'var(--sidebar-width-expanded)',
      },

      '&[data-open="true"], &:has(.sidebar:hover)': {
        '--labels-display': 'block',
        '--divider-display': 'none',
        '--sidebar-width-diff': 'calc(var(--sidebar-width-expanded) - var(--sidebar-width))',

        overflow: 'unset',
        scrollbarGutter: 'stable',
        blockSize: 'auto',
      },

      '&[data-open="true"] .sidebar': {
        gridColumn: 'sidebar',
        inlineSize: '100%',
      },

      '&[data-open="false"] .sidebar:hover': {
        gridColumn: 'sidebar-stack',
        inlineSize: 'var(--sidebar-width-expanded)',
        overflowY: 'auto',
        scrollbarGutter: 'stable',
        '--labels-display': 'block',
        '--divider-display': 'none',
      },
    },
  },
});

const topbarRecipe = defineRecipe({
  className: 'topbar',
  base: {
    gridRow: 1,
    gridColumn: 'full',
    position: 'sticky',
    top: 0,
    zIndex: '20',
  },
});

const welcomeRecipe = defineRecipe({
  className: 'welcome',
  base: {
    '--swap-slot-opacity': '0',
    anchorName: '--swap-slot',
    position: 'sticky',
    top: 'var(--topbar-height)',
    gridRow: '2',
    gridColumn: 'full',
    blockSize: 'var(--welcome-height)',
    overflow: 'clip',
    backgroundColor: 'background.brand.alt',
    color: 'text.always.light.alpha',
    alignItems: 'center',
    gap: 'space.12',
    padding: '0 space.10',
    zIndex: '11',

    transitionProperty: 'block-size, padding-block',
    transitionDelay: '.3s',
    transitionDuration: 'var(--sidebar-transition-duration)',
    transitionTimingFunction: 'cubic-bezier(0.77, 0, 0.18, 1)',

    smDown: {
      display: 'flex',
      gap: 'padding.large',
    },

    sm: {
      display: 'none',
    },

    // Na impressão a largura do papel (~698px em A4) cai em `smDown`, o que
    // faria esta faixa aparecer no relatório. Declarado por último para vencer.
    _print: {
      display: 'none',
    },
  },
});

const sidebarRecipe = defineRecipe({
  className: 'sidebar',
  base: {
    gridRow: '3',
    gridColumn: 'full',
    overflow: 'clip',
    translate: '-100% 0',
    zIndex: '10',
    transitionProperty: 'translate',
    transitionDuration: 'var(--sidebar-transition-duration)',
    transitionTimingFunction: 'cubic-bezier(0.77, 0, 0.18, 1)',
    scrollbarGutter: 'stable',

    '&[data-open="true"], &[data-open="false"]:hover': {
      translate: '0 0',
      overflowY: 'auto',
      overscrollBehavior: 'contain',

      smDown: {
        position: 'fixed',
        bottom: '0',
        top: 'calc(var(--topbar-height) + var(--welcome-height))',
        width: '100svw',
      },
    },
    sm: {
      gridRow: '2',
      gridColumn: 'sidebar',
      position: 'sticky',
      top: 'var(--topbar-height)',
      translate: '0 0',
      blockSize: 'calc(100svh - var(--topbar-height))',
      inlineSize: 'var(--sidebar-width)',
      transitionProperty: 'inline-size',
    },
  },
});

const contentRecipe = defineRecipe({
  className: 'content',
  base: {
    gridRow: '2 / -1',
    gridColumn: 'full',
    inlineSize: '100%',
    minInlineSize: '0',
    maxInlineSize: 'var(--max-content-width)',
    margin: '0 auto',
    // Offset the sticky action bar when present. --action-bar-height is set
    // by AppLayout via ResizeObserver on the ActionBarSlotFactory element.
    paddingBlockEnd: 'var(--action-bar-height, 0px)',

    sm: {
      gridRow: '2',
      gridColumn: 'content',
    },

    md: {
      '[data-open="false"] &': {
        // gridColumn: 'sidebar-end content-end',
      },
    },

    '@media print': {
      gridColumn: 'full',
      paddingInline: 0,
      maxInlineSize: 'none',
    },
  },
});

const actionBarSlotRecipe = defineRecipe({
  className: 'action-bar-slot',
  base: {
    gridRow: '2 / -1',
    gridColumn: 'full',
    alignSelf: 'end',
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    pointerEvents: 'none',

    sm: {
      gridRow: '2',
      gridColumn: 'content',
    },
  },
});

export const GridFactory = chakra('div', gridRecipe);
export const TopbarFactory = chakra(GlobalNavigation, topbarRecipe);
export const WelcomeFactory = chakra(WelcomeSection, welcomeRecipe);
export const SidebarFactory = chakra(LocalNavigation, sidebarRecipe);
export const ContentFactory = chakra('main', contentRecipe);
export const ActionBarSlotFactory = chakra('div', actionBarSlotRecipe);
